import type { RefObject, ReactNode, MutableRefObject } from 'react';

import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { checkSSRPartialSuccess } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess';

import type { LoaderResultType, LoadedModuleType, SSRSuccessCheckName } from '../types';
import { ActivationReason } from '../types';

export type GateStatus = 'suspended' | 'loading' | 'resolved';

export type GateType = {
	promise: Promise<void>;
	resolve: () => void;
	getStatus: () => GateStatus;
	setLoading: () => void;
};

export type GateComponentProps<P> = {
	gate: GateType | null;
	id: string;
	loader: () => LoaderResultType<P>;
	hostRef: RefObject<HTMLDivElement | null>;
	onLoaded: (m: LoadedModuleType<P>) => void;
	render: () => ReactNode;
	loaderPromiseRef: MutableRefObject<Promise<void> | null>;
	ssrSuccessCheckName?: SSRSuccessCheckName;
	activateComponent: (reason: ActivationReason) => void;
};

export function makeGate(): GateType {
	let resolve!: () => void;
	let status: GateStatus = 'suspended';
	const promise = new Promise<void>((r) => (resolve = r));

	const resolveOnce = () => {
		if (status !== 'resolved') {
			status = 'resolved';
			resolve();
		}
	};

	const setLoading = () => {
		if (status === 'suspended') {
			status = 'loading';
		}
	};

	return {
		promise,
		resolve: resolveOnce,
		getStatus: () => status,
		setLoading,
	};
}

export function unlockGate<P>(
	gate: GateType,
	id: string,
	loader: () => LoaderResultType<P>,
	hostRef: RefObject<HTMLDivElement | null>,
	onLoaded: (m: LoadedModuleType<P>) => void,
	loaderPromiseRef: MutableRefObject<Promise<void> | null>,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<void> {
	// Return existing promise if already loading
	if (loaderPromiseRef.current) {
		return loaderPromiseRef.current;
	}

	gate.setLoading?.();

	const promise = Promise.resolve()
		.then(() => loader())
		.then((mod) => {
			try {
				onLoaded(mod);
				if (typeof window !== 'undefined') {
					const host = hostRef?.current;
					gate.resolve();
					const root = window.__REACT_ROOT__;
					const target = host?.firstElementChild || host || undefined;
					root?.unstable_scheduleHydration?.(target);
				}
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'loadable',
						action: 'hydrationFailed',
						actionSubject: 'loadableComponent',
						attributes: {
							placeholderId: id,
							errorMessage: error instanceof Error ? error.message : 'Unknown error',
							errorStack: error instanceof Error ? error.stack : undefined,
						},
					},
				}).fire();
			}
		})
		.catch((error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'loadable',
					action: 'loadFailed',
					actionSubject: 'loadableComponent',
					attributes: {
						placeholderId: id,
						errorMessage: error instanceof Error ? error.message : 'Unknown error',
						errorStack: error instanceof Error ? error.stack : undefined,
					},
				},
			}).fire();
		});

	loaderPromiseRef.current = promise;
	return promise;
}

export function Gate<P>({
	gate,
	render,
	ssrSuccessCheckName,
	activateComponent,
}: GateComponentProps<P>): ReactNode {
	//eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR && gate && gate.getStatus() !== 'resolved') {
		// Unlock the gate immediately if we're not able to hydrate the page (or if the react-dom patch isn't present)
		// Or, if the component didn't fully render on the server (checkSSRPartialSuccess = false)
		const shouldUnlock =
			!window?.__SSR_RENDERED__ ||
			!window?.__HYDRATABLE__ ||
			window?.__HYDRATION_FAILED__ ||
			!window.__CC_REACT_DOM_CONTEXT_PATCH__ ||
			!ssrSuccessCheckName ||
			!checkSSRPartialSuccess(ssrSuccessCheckName);

		if (shouldUnlock) {
			// Trigger unlocking in the background
			activateComponent(ActivationReason.IMMEDIATE_UNLOCK);
		}
		// Keep the boundary suspended until the gate is resolved
		throw gate.promise;
	}
	return render();
}
